import {getId, getType} from 'apis/model/base'
import getAvatar from 'helpers/getAvatar'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import {permissionCodes} from 'modules/permissions/contants'
import PermissionContext from 'modules/permissions/PermissionContext'
import React, {useContext} from 'react'
import {IoNewspaperOutline} from 'react-icons/io5'
import {RiBarChart2Fill, RiUser3Fill, RiWechatLine} from 'react-icons/ri'
import {branch, compose, fromRenderProps, renderNothing} from 'recompose'
import {PrayerHandSvg} from 'components/Svgs/PrayersSvg'
import AntdConfigWrapper from 'views/AppLocals/custom/AntdConfigWrapper'
import {FeedContentTypes} from 'views/Feed/Feed'
import {NullAsDefault} from 'views/Shared'
import ServerContext from 'views/Server/ServerContext'
import {LoginContext} from 'components/LoginContext'
import {useHistory} from 'modules/navigation/useRouter'
import {Avatar} from 'antd'
import EnhancedPost, {PostTypes} from 'views/AppLocals/factory/createPost'
import ServerContainer from 'views/Server/ServerContainer'
import {useRouteSearchParam} from 'envs/_current/history'
import {UseHook} from 'components/UseReducer'
import {PostFormContext, PostFormProvider} from './PostFormContext'

// ─── Post routes (mirrors PostContainer) ─────────────────────────────────────
// Defined here so GenericPostForm is self-contained.
// If you prefer, keep using PostContainer from Post/PostContainer.js instead.

const postRoutes = [
    {
        path: 'createPost',
        name: 'Create Post',
        component: EnhancedPost(PostTypes.DEFAULT),
        exact: true,
    },
    {
        path: 'createPrayer',
        name: 'Create Prayer',
        component: EnhancedPost(PostTypes.PRAYER),
        exact: true,
    },
    {
        path: 'createAsk',
        name: 'Create Q&A',
        component: EnhancedPost(PostTypes.ASK),
        exact: true,
    },
    {
        path: 'createPoll',
        name: 'Create Poll',
        component: EnhancedPost(PostTypes.POLL),
        exact: true,
    },
]

// ─── Action definitions ───────────────────────────────────────────────────────

const POST_ACTIONS = [
    {
        type: FeedContentTypes.POLL,
        label: 'poll question',
        path: 'createPoll',
        icon: <RiBarChart2Fill size={17} className="text-green-500" />,
    },
    {
        type: FeedContentTypes.ARTICLE,
        label: 'article',
        path: null,
        icon: <IoNewspaperOutline size={17} className="text-yellow-400" />,
    },
    {
        type: FeedContentTypes.ASK,
        label: 'questions and answers',
        path: 'createAsk',
        icon: <RiWechatLine size={17} className="text-primary" />,
    },
    {
        type: FeedContentTypes.POST,
        label: 'intention',
        path: 'createPrayer',
        icon: <PrayerHandSvg size={17} className="text-indigo-700" />,
    },
]

// ─── Header ───────────────────────────────────────────────────────────────────

const PostInputHeader = ({ onClick }) => {
    const t = useTranslate()
    return (
        <LoginContext.Consumer>
            {(login) => (
                <div className="flex items-center gap-3">
                    <Avatar
                        size={32}
                        src={getAvatar(login)}
                        icon={<RiUser3Fill className="text-color-400" />}
                        className="flex flex-center background-200 rounded-full flex-shrink-0"
                    />
                    <div
                        onClick={onClick}
                        style={{ height: 34 }}
                        className="flex items-center flex-1 background-200 hover:background-100 rounded-full cursor-pointer px-4">
                        <span className="text-sm text-color-400 italic leading-tight">
                            {t('share your thoughts')}
                        </span>
                    </div>
                </div>
            )}
        </LoginContext.Consumer>
    )
}

// ─── Action button ────────────────────────────────────────────────────────────

const PostActionButton = ({ icon, label, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        style={{ height: 35, padding: '0 10px' }}
        className="flex flex-col md:flex-row items-center gap-0 md:gap-1 hover:background-200 rounded-md text-color-500 hover:text-primary cursor-pointer transition-colors">
        {icon}
        {!!label && (
            <span className="font-medium text-xs md:text-sm leading-tight truncate">
                {label}
            </span>
        )}
    </button>
)

// ─── Actions row ──────────────────────────────────────────────────────────────
// Reads ServerContext.setCurrent to trigger route switch in ServerContainer.
// Reads PostFormContext.current to get selected channel for article navigation.

const PostActions = () => {
    const t = useTranslate()
    const history = useHistory()
    const { setCurrent = NullAsDefault } = useContext(ServerContext)
    const { current } = useContext(PostFormContext)
    const selectedChannel = current?.container ?? null

    const handleAction = ({ type, path }) => {
        console.warn(11111111122, type, path)
        if (type === FeedContentTypes.ARTICLE) {
            const channelId = getId(selectedChannel)
            if (channelId) {
                history.push(`/channel/${channelId}/new-article`)
            } else {
                // No channel selected yet — open createPost so user picks one
                setCurrent('createPost')
            }
            return
        }
        if (path) {
            setCurrent(path)
        }
    }

    return (
        <div className="flex justify-around gap-2 truncate">
            {POST_ACTIONS.map((action, index) => (
                <PostActionButton
                    key={index}
                    icon={action.icon}
                    label={t(action.label)}
                    onClick={() => handleAction(action)}
                />
            ))}
        </div>
    )
}

// ─── Divider ──────────────────────────────────────────────────────────────────

const Divider = () => <div className="border-t border-color-50 my-3" />

// ─── Inner form (inside ServerContainer) ─────────────────────────────────────

const PostFormUI = () => {
    const { setCurrent = NullAsDefault } = useContext(ServerContext)
    return (
        <div className="flex flex-col p-3 background border border-color-50 shadow-items-md rounded-2xl">
            <PostInputHeader onClick={() => {
                setCurrent('createPost')
            }} />
            <Divider />
            <PostActions />
        </div>
    )
}

// ─── Main ─────────────────────────────────────────────────────────────────────

/**
 * GenericPostForm
 *
 * Full self-contained replacement for InputBoardForm.
 *
 * Layer structure:
 *   PostFormProvider          — channel fetch + selection state (PostFormContext)
 *     UseHook(initialCurrent) — read initialCurrent from route search param
 *       ServerContainer       — manages setCurrent + renders post routes via Switch
 *         PostFormUI          — header + action buttons
 *
 * Props:
 *   container      – { id, type } scope for channel fetch (org/channel)
 *   defaultChannel – full channel object to pre-select on mount (optional)
 *
 * Usage:
 *   <GenericPostForm
 *       container={{ type: getType(organization), id: getId(organization) }}
 *       defaultChannel={channelObject}
 *   />
 */
const GenericPostForm = ({ container, defaultChannel }) => {
    const login = useContext(LoginContext)
    if (!login) return null

    const scopeId   = getId(container)
    const scopeType = getType(container)

    return (
        <PostFormProvider
            container={container}
            defaultChannel={defaultChannel}>
            <UseHook
                hook={useRouteSearchParam}
                deps={['initialCurrent']}>
                {(initialCurrent) => (
                    <ServerContainer
                        id={scopeId}
                        type={scopeType}
                        routes={postRoutes}
                        initialCurrent={initialCurrent}>
                        <PostFormUI />
                    </ServerContainer>
                )}
            </UseHook>
        </PostFormProvider>
    )
}

// ─── Export with permission guard ─────────────────────────────────────────────

export default compose(
    (C) => (props) => <AntdConfigWrapper><C {...props} /></AntdConfigWrapper>,
    fromRenderProps(PermissionContext.Consumer, (props) => props),
    branch(
        (props) => !Boolean(_.get(props, permissionCodes.canPost)),
        renderNothing
    )
)(GenericPostForm)